import { Flex, Heading, HStack, Text } from '@chakra-ui/react';
import { PropsWithChildren } from 'react';
import { Progress } from './Progress';
import { FormattedMessage } from 'react-intl';
import { FormattedMessageProps } from 'types/utils';
import { HelperText } from './wizards/pcw/HelperText';
import ShowHideSidebarButton from 'components/layout/ShowHideSidebarButton';

interface WizardFormWrapperProps extends PropsWithChildren {
    title: string | FormattedMessageProps;
    subTitle?: string | FormattedMessageProps;
    description?: string | FormattedMessageProps;
    helperText?: string | FormattedMessageProps;
    percent: number;
    showDetailsInWrapper: boolean;
}

/**
 * WizardWrapper provides the layout structure and header content for wizard steps.
 *
 * Renders a consistent layout with:
 * - Progress bar at the top showing completion percentage
 * - Main title (always displayed)
 * - Optional subtitle, description, and helper text (conditionally displayed based on showDetailsInWrapper)
 * - Flexible content area for step-specific components
 *
 * Supports both plain strings and internationalized messages via FormattedMessage.
 * The wrapper uses flexbox layout to ensure the content area takes remaining space
 * while keeping the header fixed at the top.
 *
 * @param title - Main heading text, required for all wizard steps
 * @param subTitle - Optional secondary heading text
 * @param description - Optional detailed description text
 * @param helperText - Optional helper/instructional text rendered in styled component
 * @param percent - Progress completion percentage (0-100) for the progress bar
 * @param showDetailsInWrapper - Controls visibility of subtitle, description, and helperText
 * @param children - Step-specific content rendered in the flexible content area
 */
export function WizardWrapper({
    children,
    title,
    subTitle,
    description,
    helperText,
    percent,
    showDetailsInWrapper,
}: WizardFormWrapperProps) {
    return (
        <Flex position="relative" flexDir="column" h="full" w="full" p={4} px={8}>
            <Flex flexDir="column" flexShrink={0}>
                <HStack>
                    <ShowHideSidebarButton includeMessage={false} />
                    <Progress value={percent} />
                </HStack>
                <Heading as="h2" size="md" mb={2}>
                    {typeof title === 'string' ? title : <FormattedMessage {...title} />}
                </Heading>
                {showDetailsInWrapper && subTitle && (
                    <Text fontSize="lg" mb={4} color="black">
                        {typeof subTitle === 'string' ? subTitle : <FormattedMessage {...subTitle} />}
                    </Text>
                )}
                {showDetailsInWrapper && description && (
                    <Text fontSize="larger" mb={4} color="black">
                        {typeof description === 'string' ? description : <FormattedMessage {...description} />}
                    </Text>
                )}
                {showDetailsInWrapper && helperText && (
                    <HelperText>
                        {typeof helperText === 'string' ? helperText : <FormattedMessage {...helperText} />}
                    </HelperText>
                )}
            </Flex>
            <Flex flexDir="column" flex="1" mt={4}>
                {children}
            </Flex>
        </Flex>
    );
}
